import {LatLng} from 'leaflet';
import {IPositionSingleVoyage} from "@containers/singleVoyage/types/position";

/**
 * A new route point farther than this from the old line belongs to a changed stretch. Both lines
 * come from the same router, so on an unchanged stretch they coincide and a small value stays quiet.
 */
const CHANGED_TRACK_METRES = 8
/** A stop is taken as moved when the old line misses it by this much more than the new one. */
const MOVED_STOP_GAP_METRES = 8
const MOVED_STOP_MIN_METRES = 15
/** A stop shifted at least this far from its remembered spot is moved, whatever the lines do. */
const SHIFTED_STOP_METRES = 5

const METRES_PER_DEGREE = 111_320

interface IPlanePoint {
	x: number
	y: number
}

/** Local equirectangular projection: plenty for distances of a few kilometres. */
const toPlane = (lat: number, lng: number, cosLat: number): IPlanePoint => ({
	x: lng * METRES_PER_DEGREE * cosLat,
	y: lat * METRES_PER_DEGREE,
})

const distanceToLine = (point: IPlanePoint, line: IPlanePoint[]): number => {
	let best = Infinity
	for (let i = 0; i < line.length - 1; i++) {
		const a = line[i]
		const b = line[i + 1]
		const dx = b.x - a.x
		const dy = b.y - a.y
		const lengthSquared = dx * dx + dy * dy
		const t = lengthSquared === 0
			? 0
			: Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared))
		const distance = Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy))
		if (distance < best) {
			best = distance
		}
	}
	return best
}

export interface IRouteChangeVoyageTemplate {
	/** stretches of the new route that the old one did not run along */
	changedSegments: LatLng[][]
	/** the old route where it ran apart from the new one */
	previousSegments: LatLng[][]
	/** ids of stops the new route reaches and the old one passed by */
	movedPointIds: Set<string>
	/** the recorded old position of each moved stop that has one */
	previousStopPositions: Map<string, LatLng>
}

/**
 * Compares the rebuilt route with the one the template had. Only stops that were on the route at
 * rebuild time are judged, so a stop added afterwards is not mistaken for a moved one.
 */
export const buildRouteChangeVoyageTemplate = ({previous, current, points}: {
	previous: IPositionSingleVoyage[]
	current: IPositionSingleVoyage[]
	/** origin: where the stop stood before its last move, when this browser saw the move */
	points: { id: string | number, lat: number, lng: number, origin?: { lat: number, lng: number } }[]
}): IRouteChangeVoyageTemplate => {
	if (previous.length < 2 || current.length < 2) {
		return {changedSegments: [], previousSegments: [], movedPointIds: new Set(), previousStopPositions: new Map()}
	}

	const cosLat = Math.cos((previous[0].lat * Math.PI) / 180)
	const oldLine = previous.map((it) => toPlane(it.lat, it.lng, cosLat))
	const newLine = current.map((it) => toPlane(it.lat, it.lng, cosLat))

	const changedSegments = divergingRuns(current, newLine, oldLine)
	const previousSegments = divergingRuns(previous, oldLine, newLine)

	const movedPointIds = new Set<string>()
	const previousStopPositions = new Map<string, LatLng>()
	points.forEach((point) => {
		const plane = toPlane(point.lat, point.lng, cosLat)
		// a remembered spot is exact; a stop moved along the same road leaves the lines alike
		if (point.origin) {
			const before = toPlane(point.origin.lat, point.origin.lng, cosLat)
			if (Math.hypot(plane.x - before.x, plane.y - before.y) >= SHIFTED_STOP_METRES) {
				movedPointIds.add(String(point.id))
				previousStopPositions.set(String(point.id), new LatLng(point.origin.lat, point.origin.lng))
				return
			}
		}
		const toOld = distanceToLine(plane, oldLine)
		const toNew = distanceToLine(plane, newLine)
		if (toOld > MOVED_STOP_MIN_METRES && toOld > toNew + MOVED_STOP_GAP_METRES) {
			movedPointIds.add(String(point.id))
		}
	})

	return {changedSegments, previousSegments, movedPointIds, previousStopPositions}
}

/** Runs of {@code route} that stray from {@code other}, each joined to the shared route at both ends. */
const divergingRuns = (route: IPositionSingleVoyage[], line: IPlanePoint[], other: IPlanePoint[]): LatLng[][] => {
	const apart = line.map((point) => distanceToLine(point, other) > CHANGED_TRACK_METRES)

	const runs: LatLng[][] = []
	let start = -1
	apart.forEach((isApart, index) => {
		if (isApart && start === -1) {
			start = index
		}
		if (start !== -1 && (!isApart || index === apart.length - 1)) {
			const end = isApart ? index : index - 1
			const from = Math.max(0, start - 1)
			const to = Math.min(route.length - 1, end + 1)
			if (to > from) {
				runs.push(route.slice(from, to + 1).map((it) => new LatLng(it.lat, it.lng)))
			}
			start = -1
		}
	})
	return runs
}
